/**
 * @fileoverview Servizio per gruppi e membri (groups, groupMembers).
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  COLLECTIONS,
  GROUPS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  GROUP_MEMBER_STATUS,
  GROUP_MEMBER_ROLES,
} from '../domain/dataModel';

/**
 * Crea un nuovo gruppo e aggiunge l'owner come primo membro (ACTIVE, OWNER).
 * Verifica che non esista già un gruppo con lo stesso nome (case-sensitive).
 * @param {string} name - Nome del gruppo
 * @param {string} ownerId - UID del proprietario
 * @returns {Promise<{ groupId: string }>}
 */
export async function createGroup(name, ownerId) {
  try {
    const trimmed = (name || '').trim();
    if (!trimmed) throw new Error('Inserisci un nome per il gruppo.');
    if (!ownerId) throw new Error('Proprietario mancante.');

    const existing = await getDocs(
      query(
        collection(db, COLLECTIONS.GROUPS),
        where(GROUPS_FIELDS.NAME, '==', trimmed)
      )
    );
    if (!existing.empty) throw new Error('Esiste già un gruppo con questo nome.');

    const groupRef = await addDoc(collection(db, COLLECTIONS.GROUPS), {
      [GROUPS_FIELDS.NAME]: trimmed,
      [GROUPS_FIELDS.NAME_LOWER]: trimmed.toLowerCase(),
      [GROUPS_FIELDS.OWNER_ID]: ownerId,
      [GROUPS_FIELDS.CREATED_AT]: serverTimestamp(),
      [GROUPS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });

    await addDoc(collection(db, COLLECTIONS.GROUP_MEMBERS), {
      [GROUP_MEMBERS_FIELDS.GROUP_ID]: groupRef.id,
      [GROUP_MEMBERS_FIELDS.USER_ID]: ownerId,
      [GROUP_MEMBERS_FIELDS.ROLE]: GROUP_MEMBER_ROLES.OWNER,
      [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.ACTIVE,
      [GROUP_MEMBERS_FIELDS.JOINED_AT]: serverTimestamp(),
      [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });

    return { groupId: groupRef.id };
  } catch (err) {
    throw new Error(err?.message || 'Errore nella creazione del gruppo.');
  }
}

/**
 * Ricerca gruppi per nome (case-insensitive, prefix su nameLower).
 * @param {string} queryText - Testo di ricerca
 * @returns {Promise<Array<{ id: string, ...Object }>>}
 */
export async function searchGroups(queryText) {
  try {
    const term = (queryText || '').trim().toLowerCase();
    if (!term) return [];

    const q = query(
      collection(db, COLLECTIONS.GROUPS),
      where(GROUPS_FIELDS.NAME_LOWER, '>=', term),
      where(GROUPS_FIELDS.NAME_LOWER, '<=', term + '\uf8ff')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    throw new Error(err?.message || 'Errore nella ricerca dei gruppi.');
  }
}

/**
 * Recupera un gruppo per ID.
 * @param {string} groupId
 * @returns {Promise<{ id: string, ...Object } | null>}
 */
export async function getGroupById(groupId) {
  try {
    if (!groupId) return null;
    const ref = doc(db, COLLECTIONS.GROUPS, groupId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento del gruppo.');
  }
}

/**
 * Aggiorna campi di un gruppo.
 * @param {string} groupId
 * @param {Object} data - Campi da aggiornare
 * @returns {Promise<void>}
 */
export async function updateGroup(groupId, data) {
  try {
    if (!groupId) throw new Error('ID gruppo mancante.');
    const ref = doc(db, COLLECTIONS.GROUPS, groupId);
    await updateDoc(ref, {
      ...data,
      [GROUPS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });
  } catch (err) {
    throw new Error(err?.message || 'Errore nell\'aggiornamento del gruppo.');
  }
}

/**
 * Recupera i membri attivi del gruppo (status ACTIVE).
 * @param {string} groupId
 * @returns {Promise<Array<{ id: string, ...Object }>>}
 */
export async function getActiveMembers(groupId) {
  try {
    if (!groupId) return [];
    const q = query(
      collection(db, COLLECTIONS.GROUP_MEMBERS),
      where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', groupId),
      where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento dei membri.');
  }
}

/**
 * Aggiunge un membro utente al gruppo (invito: status INVITED, role MEMBER).
 * @param {string} groupId
 * @param {string} userId - UID utente
 * @param {string} [role] - Ruolo (default MEMBER)
 * @returns {Promise<{ memberId: string }>}
 */
export async function addMember(groupId, userId, role = GROUP_MEMBER_ROLES.MEMBER) {
  try {
    if (!groupId || !userId) throw new Error('Gruppo o utente mancante.');
    const memberRef = await addDoc(collection(db, COLLECTIONS.GROUP_MEMBERS), {
      [GROUP_MEMBERS_FIELDS.GROUP_ID]: groupId,
      [GROUP_MEMBERS_FIELDS.USER_ID]: userId,
      [GROUP_MEMBERS_FIELDS.ROLE]: role,
      [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.INVITED,
      [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });
    return { memberId: memberRef.id };
  } catch (err) {
    throw new Error(err?.message || 'Errore nell\'aggiunta del membro.');
  }
}

/**
 * Aggiunge un ospite al gruppo e, se esiste un periodo attivo, crea il rating iniziale.
 * @param {string} groupId
 * @param {string} guestNickname - Nome dell'ospite
 * @param {number} [initialRating] - Punti iniziali (default 1000, clamp 0-3000)
 * @param {string|null} [activePeriodId] - ID periodo attivo; se fornito, crea il rating
 * @returns {Promise<{ memberId: string }>}
 */
export async function addGuestMember(groupId, guestNickname, initialRating = 1000, activePeriodId = null) {
  try {
    const nickname = (guestNickname || '').trim();
    if (!nickname) throw new Error('Inserisci il nickname dell\'ospite.');
    if (!groupId) throw new Error('Gruppo mancante.');

    const { createRating } = await import('./ratingService');
    const memberRef = await addDoc(collection(db, COLLECTIONS.GROUP_MEMBERS), {
      [GROUP_MEMBERS_FIELDS.GROUP_ID]: groupId,
      [GROUP_MEMBERS_FIELDS.USER_ID]: null,
      [GROUP_MEMBERS_FIELDS.IS_GUEST]: true,
      [GROUP_MEMBERS_FIELDS.GUEST_NICKNAME]: nickname,
      [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.ACTIVE,
      [GROUP_MEMBERS_FIELDS.ROLE]: GROUP_MEMBER_ROLES.MEMBER,
      [GROUP_MEMBERS_FIELDS.JOINED_AT]: serverTimestamp(),
      [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });

    if (activePeriodId) {
      const points = Math.min(3000, Math.max(0, Number(initialRating) || 1000));
      await createRating(groupId, activePeriodId, memberRef.id, null, points);
    }
    return { memberId: memberRef.id };
  } catch (err) {
    throw new Error(err?.message || 'Errore nella creazione dell\'ospite.');
  }
}

/**
 * Aggiorna lo stato di un membro (es. ACTIVE, REJECTED).
 * @param {string} memberId - ID documento groupMembers
 * @param {string} status - Nuovo status (GROUP_MEMBER_STATUS)
 * @returns {Promise<void>}
 */
export async function updateMemberStatus(memberId, status) {
  try {
    if (!memberId) throw new Error('ID membro mancante.');
    const ref = doc(db, COLLECTIONS.GROUP_MEMBERS, memberId);
    const updates = { [GROUP_MEMBERS_FIELDS.STATUS]: status, [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp() };
    if (status === GROUP_MEMBER_STATUS.ACTIVE) {
      updates[GROUP_MEMBERS_FIELDS.JOINED_AT] = serverTimestamp();
    }
    await updateDoc(ref, updates);
  } catch (err) {
    throw new Error(err?.message || 'Errore nell\'aggiornamento dello stato del membro.');
  }
}

/**
 * Rimuove un membro: imposta status REJECTED e disattiva i suoi rating nel periodo attivo.
 * Carica il documento membro per ottenere groupId e userId; recupera il periodo attivo.
 * @param {string} memberId - ID documento groupMembers
 * @returns {Promise<void>}
 */
export async function removeMember(memberId) {
  try {
    if (!memberId) throw new Error('ID membro mancante.');
    const memberRef = doc(db, COLLECTIONS.GROUP_MEMBERS, memberId);
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) throw new Error('Membro non trovato.');
    const memberData = memberSnap.data();
    const groupId = memberData[GROUP_MEMBERS_FIELDS.GROUP_ID];
    await updateDoc(memberRef, {
      [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.REJECTED,
      [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });
    if (groupId) {
      const { getActivePeriod } = await import('./periodService');
      const { deactivateRating } = await import('./ratingService');
      const activePeriod = await getActivePeriod(groupId);
      if (activePeriod?.id) {
        await deactivateRating(
          memberId,
          memberData[GROUP_MEMBERS_FIELDS.USER_ID] ?? null,
          activePeriod.id,
          groupId
        );
      }
    }
  } catch (err) {
    throw new Error(err?.message || 'Errore durante la rimozione del membro.');
  }
}

/**
 * Trasferisce la proprietà del gruppo a un nuovo owner (solo update ownerId su groups).
 * @param {string} groupId
 * @param {string} newOwnerId - UID del nuovo proprietario
 * @returns {Promise<void>}
 */
export async function transferOwnership(groupId, newOwnerId) {
  try {
    if (!groupId || !newOwnerId) throw new Error('Gruppo o nuovo proprietario mancante.');
    await updateGroup(groupId, { [GROUPS_FIELDS.OWNER_ID]: newOwnerId });
  } catch (err) {
    throw new Error(err?.message || 'Errore durante l\'assegnazione del proprietario.');
  }
}
